import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';

import { Link } from 'libs/router';

import ActionButton from 'components/buttons/action-button/ActionButton';
import { withModal } from 'components/modal/ModalHOC';
import ShippingModal from 'modules/shipping/components/shipping-modal/ShippingModal';
import SubcampaignOfferDeclineModal from 'modules/subcampaigns/components/offer-decline-modal/SubcampaignOfferDeclineModal';

import { putOfferState } from 'modules/subcampaigns/reducers/offer';
import { fetchSubcampaign } from 'modules/subcampaigns/reducers/subcampaign';

import { __t, applyTranslations } from 'utils/language';
import { isInfluencerProfileRejected } from 'utils/profile';
import { insertIf } from 'utils/list';
import {
  SUBCAMPAIGN_STATE_ACCEPTED,
  isDraft,
  isNegotiationsReopened,
  isPending,
  isQuote,
  isQuoteProvided,
  isRevoked,
  shouldAskForShippingAddress
} from 'utils/subcampaign';
import { getSelectedSubcampaigns } from 'utils/bulk-offer';

import type {
  BrandProfile,
  Subcampaign,
  SubcampaignInfluencerProfile,
  SubcampaignOfferState
} from 'types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./offer-card-footer.i18n.json'));

const AcceptButtonWithModal = withModal(ActionButton, {
  modalContent: ShippingModal
});

const DeclineButtonWithModal = withModal(ActionButton, {
  modalContent: SubcampaignOfferDeclineModal
});

type OfferCardAction = {
  color: 'primary' | 'secondary' | 'tertiary',
  label: string,
  icon: string,
  url?: string,
  isDisabled?: boolean,
  modalType?: 'accept' | 'decline' | 'bulk_offer_preview',
  onClick?: (a: Subcampaign) => void
};

type OwnProps = {
  canEdit?: boolean,
  influencerProfile?: SubcampaignInfluencerProfile,
  brandProfile?: BrandProfile,
  subcampaign: Subcampaign,
  isBulkOfferActive: boolean,
  isForBrand: boolean,
  isSubmitButtonDisabled?: boolean,
  submitButtonLabel: string | undefined | null,
  onSaveAsDraft?: () => Promise<void>,
  onSendOffer?: () => Promise<void>,
  onEditClick?: () => void,
  allowEditAction: boolean,
  onProvideQuoteClick?: () => void
};

type DefaultProps = {
  layout?: string
};

type StateProps = {
  isMultipleCreatorsSelected: boolean
};

type DispatchProps = {
  dispatchPutOfferState: (b: number, a: SubcampaignOfferState) => Promise<void>,
  dispatchFetchSubcampaign: (a: number) => Promise<void>
};

type Props = OwnProps & DefaultProps & StateProps & DispatchProps;

class OfferCardFooter extends React.PureComponent<Props> {
  static defaultProps: DefaultProps = {
    layout: 'vertical'
  };

  handleAcceptRenegotiatedSubcampaign = () => {
    const { subcampaign, dispatchPutOfferState, dispatchFetchSubcampaign } = this.props;

    if (subcampaign.updatedOffer) {
      dispatchPutOfferState(subcampaign.updatedOffer.id, { state: SUBCAMPAIGN_STATE_ACCEPTED })
        .then(() => dispatchFetchSubcampaign(subcampaign.id));
    }
  };

  handleProvideQuote = () => {
    const { onProvideQuoteClick } = this.props;

    if (onProvideQuoteClick) {
      onProvideQuoteClick();
    }
  };

  getBrandActions(): Array<any> {
    const {
      canEdit,
      influencerProfile,
      subcampaign,
      isSubmitButtonDisabled,
      submitButtonLabel,
      onSendOffer,
      onEditClick,
      allowEditAction,
      isBulkOfferActive,
      isMultipleCreatorsSelected,
      onSaveAsDraft
    } = this.props;

    if (isInfluencerProfileRejected(influencerProfile)) {
      return [];
    }

    if (isBulkOfferActive) {
      return [
        {
          color: 'tertiary',
          label: __t('collabary_common.action_button.cancel'),
          icon: '',
          url: `/campaigns/${subcampaign.campaign.id}/participants/shortlisted`
        },
        ...insertIf(!isMultipleCreatorsSelected, {
          color: 'secondary',
          label: __t('offer_card_footer.button_save_as_draft'),
          icon: '',
          isDisabled: false,
          onClick: onSaveAsDraft
        }),
        {
          color: 'primary',
          label: submitButtonLabel,
          icon: '',
          isDisabled: isSubmitButtonDisabled,
          onClick: onSendOffer
        }
      ];
    }

    if (influencerProfile && isDraft(subcampaign)) {
      return [
        {
          color: 'tertiary',
          label: __t('collabary_common.action_button.cancel'),
          icon: '',
          url: `/campaigns/${subcampaign.campaign.id}/participants/shortlisted`
        },
        ...insertIf(!isMultipleCreatorsSelected, {
          color: 'secondary',
          label: __t('offer_card_footer.button_save_as_draft'),
          icon: '',
          isDisabled: false,
          onClick: onSaveAsDraft
        }),
        {
          color: 'primary',
          label: __t('offer_card_footer.button_send'),
          icon: '',
          isDisabled: isSubmitButtonDisabled,
          onClick: onSendOffer
        }
      ];
    }

    if (influencerProfile && isRevoked(subcampaign) && !canEdit) {
      return [
        ...insertIf(allowEditAction, {
          color: 'primary',
          label: __t('offer_card_footer.button_uncancel'),
          icon: '',
          onClick: onEditClick
        })
      ];
    }

    if (influencerProfile && !isDraft(subcampaign) && canEdit) {
      return [
        {
          color: 'tertiary',
          label: __t('collabary_common.action_button.cancel'),
          icon: '',
          url: `/campaigns/${subcampaign.campaign.id}/subcampaigns/${subcampaign.id}`
        },
        {
          color: 'primary',
          label: submitButtonLabel || __t('offer_card_footer.resend_label'),
          icon: '',
          isDisabled: isSubmitButtonDisabled,
          onClick: onSendOffer
        },
        {
          color: 'secondary',
          label: __t('offer_card_footer.message_creator_label'),
          icon: '',
          url: `/campaigns/${subcampaign.campaign.id}/messages?subcampaignId=${subcampaign.id}`
        }
      ];
    }

    if (influencerProfile && !isDraft(subcampaign) && !canEdit) {
      return [
        ...insertIf(allowEditAction, {
          color: 'secondary',
          label: __t('offer_card_footer.edit_offer_label'),
          icon: '',
          onClick: onEditClick
        }),
        {
          color: isQuoteProvided(subcampaign) ? 'tertiary' : 'primary',
          label: __t('offer_card_footer.message_creator_label'),
          icon: '',
          url: `/campaigns/${subcampaign.campaign.id}/messages?subcampaignId=${subcampaign.id}`
        }
      ];
    }

    return [];
  }

  getNonBrandActions(): Array<any> {
    const {
      brandProfile,
      influencerProfile,
      subcampaign
    } = this.props;

    const declineAction = {
      color: 'secondary',
      label: __t('offer_card_footer.decline_label'),
      icon: '',
      modalType: 'decline'
    };

    const acceptLabel = __t('offer_card_footer.accept_label');

    if (brandProfile) {
      if (isInfluencerProfileRejected(influencerProfile)) {
        return [];
      }

      if (isDraft(subcampaign)) {
        return [];
      }

      if (isRevoked(subcampaign)) {
        return [
          {
            color: 'primary',
            label: __t('offer_card_footer.message_brand_label'),
            icon: '',
            url: `/campaigns/${subcampaign.campaign.id}/details/${subcampaign.id}/messages`
          }
        ];
      }

      if (isPending(subcampaign) && isQuote(subcampaign)) {
        return !subcampaign.offer.quote ? [
          declineAction,
          {
            color: 'primary',
            label: __t('offer_card_footer.send_quote_label'),
            icon: 'speech-bubble-custom-bg',
            onClick: this.handleProvideQuote
          }
        ] : [
          {
            color: 'primary',
            label: __t('offer_card_footer.message_brand_label'),
            icon: '',
            url: `/campaigns/${subcampaign.campaign.id}/details/${subcampaign.id}/messages`
          }
        ];
      }

      if (isNegotiationsReopened(subcampaign)) {
        const showShippingModal = shouldAskForShippingAddress(subcampaign);

        return [
          {
            color: 'secondary',
            label: __t('offer_card_footer.decline_renegotiation_label'),
            icon: '',
            modalType: 'decline'
          },
          ...insertIf(!showShippingModal, {
            color: 'primary',
            label: acceptLabel,
            icon: '',
            onClick: this.handleAcceptRenegotiatedSubcampaign
          }),
          ...insertIf(showShippingModal, {
            color: 'primary',
            label: acceptLabel,
            icon: '',
            modalType: 'accept'
          })
        ];
      }

      if (isPending(subcampaign) && !isQuote(subcampaign)) {
        return [
          {
            color: 'tertiary',
            label: __t('offer_card_footer.message_brand_label'),
            icon: '',
            url: `/campaigns/${subcampaign.campaign.id}/details/${subcampaign.id}/messages`
          },
          declineAction,
          {
            color: 'primary',
            label: acceptLabel,
            icon: '',
            modalType: 'accept'
          }
        ];
      }

      return [
        {
          color: 'primary',
          label: __t('offer_card_footer.message_brand_label'),
          icon: '',
          url: `/campaigns/${subcampaign.campaign.id}/details/${subcampaign.id}/messages`
        }
      ];
    }

    return [];
  }

  getActions(): Array<any> {
    const {
      isForBrand
    } = this.props;

    if (isForBrand) {
      return this.getBrandActions();
    }

    return this.getNonBrandActions();
  }

  renderLinkButton = (action: OfferCardAction): JSX.Element => (
    <Link
      to={action.url}
      key={action.label}
    >
      <ActionButton
        width="maximum"
        color={action.color}
        data={{ qa: 'offer-card-footer__button-message' }}
      >
        {this.renderButtonLabel(action)}
      </ActionButton>
    </Link>
  );

  renderModalButton = (action: OfferCardAction): JSX.Element => {
    const { subcampaign } = this.props;

    if (action.modalType === 'bulk_offer_preview') {
      return (
        <ActionButton
          width="maximum"
          color={action.color}
          disabled={action.isDisabled}
        >
          {this.renderButtonLabel(action)}
        </ActionButton>
      );
    }

    if (action.modalType === 'accept') {
      return (
        <AcceptButtonWithModal
          width="maximum"
          color={action.color}
          modalContentProps={{
            subcampaign
          }}
          data={{ qa: 'offer-card-footer__button-accept' }}
        >
          {this.renderButtonLabel(action)}
        </AcceptButtonWithModal>
      );
    }

    return (
      <DeclineButtonWithModal
        width="maximum"
        color={action.color}
        modalContentProps={{
          subcampaign
        }}
      >
        {this.renderButtonLabel(action)}
      </DeclineButtonWithModal>
    );
  };

  renderOnClickButton = (action: OfferCardAction): JSX.Element => (
    <ActionButton
      width="maximum"
      color={action.color}
      disabled={action.isDisabled}
      onClick={action.onClick}
    >
      {this.renderButtonLabel(action)}
    </ActionButton>
  );

  renderButtonLabel = (action: OfferCardAction): JSX.Element => <span>{action.label}</span>;

  render(): React.ReactNode {
    const {
      influencerProfile,
      brandProfile,
      layout
    } = this.props;

    if (!influencerProfile && !brandProfile) {
      return null;
    }

    const actions = this.getActions();
    const hasActions = actions && actions.length > 0;

    if (!hasActions) {
      return null;
    }

    const classes = classnames('offer-card-footer', {
      [`offer-card-footer--${layout}`]: layout
    });

    return (
      <div className={classes}>
        {
          actions.map((action, index) => (
            <div
              className="offer-card-footer__action"
              key={`offer-card-footer__action-${index}`}
            >
              {action.url && this.renderLinkButton(action)}
              {action.modalType && this.renderModalButton(action)}
              {action.onClick && this.renderOnClickButton(action)}
            </div>
          ))
        }
      </div>
    );
  }
}

const mapStateToProps = ({ bulkOffer }): StateProps => {
  const isMultipleCreatorsSelected = getSelectedSubcampaigns(
    bulkOffer.shortlistedSubcampaigns, bulkOffer.selectedIdsMap
  ).length > 1;

  return {
    isMultipleCreatorsSelected
  };
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchPutOfferState: (offerId: number, offerState: SubcampaignOfferState): Promise<void> => dispatch(putOfferState(offerId, offerState)),
  dispatchFetchSubcampaign: (subcampaignId: number): Promise<void> => dispatch(fetchSubcampaign(subcampaignId))
});

export default connect(mapStateToProps, mapDispatchToProps)(OfferCardFooter);
